import type { Ref } from "react";
import type { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import type { VideoInputProps } from "@/components/VideoInput/VideoInput";
import { VideoInput } from "@/components/VideoInput/VideoInput";
import { mergeRefs } from "@/helpers/mergeRefs";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormVideoInputProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<VideoInputProps, "className" | "onChange" | "value"> {
  name: TName;
  rules?: RegisterOptions<TFormValues, TName>;
  "data-testid"?: string;
  initialFileUrl?: string;
  nOfVideos?: number;
  texts?: { add: string };
  shouldSkipProcessing?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function FormVideoInput<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({ "data-testid": dataTestid, name, rules, ref, ...props }: FormVideoInputProps<TFormValues, TName>): React.ReactNode {
  const {
    field,
    fieldState: { error },
  } = useController<TFormValues, TName>({
    name,
    rules,
    defaultValue: [] as any,
  });

  return (
    <FormErrorWrapper<TFormValues> name={name} data-testid={dataTestid}>
      <VideoInput
        {...props}
        {...field}
        ref={mergeRefs(field.ref, ref)}
        aria-invalid={error != null}
        value={field.value}
        onChange={field.onChange}
        onBlur={() => {
          field.onBlur();
          props.onBlur?.();
        }}
      />
    </FormErrorWrapper>
  );
}
